import React, { useCallback, useEffect, useRef, useState } from 'react';
import parse from 'style-to-object';
import './draggable-entity-card.css';
import {
	ENTITY_FA_ICON_NAMES,
	getCanvasEntityDOMId,
	resolveNestedConditionCardColor
} from '../../services/editor/ui';
import { Canvas } from '../../lib/canvas';
import {
	ActionDescriptorState,
	ActionInputMapState,
	addDisabledReasons,
	CanvasEntityDisabledReason,
	ALL_CONNECTION_DISABLED_REASONS,
	BUILT_IN_BASE_ENTITY_IDS,
	CanvasEntityConnectionDisabledReason,
	CanvasEntityOtherDisabledReason,
	CanvasEntityTestingDisabledReason,
	ChangeSet,
	ChangeSetEntityChangeType,
	ConditionState,
	createStateFromTypeAndSubscribe,
	EntityType,
	GroupOperatorTypes,
	IActionDescriptorTransfer,
	IValueDescriptorTransfer,
	lowercaseFirstLetter,
	OperationState,
	ProjectState,
	READABLE_ENTITY_TYPES,
	removeDisabledReasons,
	resolveCardSubheader,
	ValueDescriptorState,
	StandaloneOperatorTypes,
	StateMutationAction,
	safeResolveEntityName
} from '@elyx-code/project-logic-tree';
import { ConnectionNodeType, EditorService } from '../../services/editor';
import { CanvasEntityElement } from '../../services/editor/CanvasElement';
import NewDynamicInputButton from './new-card-input-button/NewDynamicInputButton';
import Button from '../../lib/button/Button';
import InputMapProperty from './InputMapProperty';
import LogicErrorIconMenu from './logic-error/LogicErrorIconMenu';
import CanvasDropdownMenuCard from '../../lib/dropdown/CanvasDropdownMenuCard';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import CanvasPopup, { useCanvasPopup } from '../../services/editor/CanvasPopup';
import { popupNotification } from '../../popup-notification';

interface INestedConditionProps {
	entity: ConditionState;
	canvasElement: CanvasEntityElement;
	project: EditorService;
	parentDisabledReasons: CanvasEntityDisabledReason[];
}

export default function NestedCondition(props: INestedConditionProps) {
	const rootElementRef = useRef<HTMLDivElement>(null);

	const handleEdit = useCallback(async () => {
		props.project?.canvas?.unselectAll();

		EditorService.navigate(
			`${props.project?.editorBasePath}/entity/${props.entity.type}/${props.entity.id}`
		);
	}, [props.project, props.entity]);

	useEffect(() => {
		const el = rootElementRef.current as any as HTMLElement;
		if (!el) return;

		const handleDoubleClick = (e: MouseEvent) => {
			if ((e as any).__canvasIgnore) return;
			(e as any).__canvasIgnore = true;
			handleEdit();
		};

		const handleMouseDown = (e: MouseEvent) => {
			if ((e as any).__canvasIgnore) return;
			(e as any).__canvasIgnore = true;
		};

		el.addEventListener('dblclick', handleDoubleClick, false);
		el.addEventListener('mousedown', handleMouseDown, false);

		return () => {
			el.removeEventListener('dblclick', handleDoubleClick, false);
			el.removeEventListener('mousedown', handleMouseDown, false);
		};
	}, [handleEdit]);

	const [disabledReasons, setDisabledReasons] = useState<
		CanvasEntityDisabledReason[]
	>([]);

	const [updateKey, setValue] = useState(0);

	const useForceUpdate = () => {
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	const memoizedCanvasPopupBody = useCallback(
		(popup: CanvasPopup) => (
			<CanvasPopupBaseComponent
				id={
					'canvas-small-entity-card-menu-button-' +
					props.entity.id +
					'--popup--body'
				}
			>
				<CanvasDropdownMenuCard
					options={[
						{
							project: props.project,
							onClick: async () => {
								await props.project.remove(props.entity.id);
							},
							label: 'Delete',
							color: 'error',
							iconEnd: 'fa-solid fa-trash-can',
							disabled: !entity.deletable,
							tooltip: !entity.deletable
								? 'This nested condition cannot be deleted. Because the system needs to refer to it over time.'
								: 'Forever delete this nested condition',
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--delete'
						},
						{
							project: props.project,
							onClick: async () => {
								props.project?.canvas?.unselectAll();

								EditorService.navigate(
									`${props.project?.editorBasePath}/entity/${props.entity.type}/${props.entity.id}`
								);
							},
							label: 'Edit',
							iconEnd: 'fa-solid fa-pencil',
							disabled: false,
							tooltip: !props.entity.editable
								? `This ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[props.entity.type]
											.singular
									)} has been locked and can no longer be edited`
								: `Edit this ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[props.entity.type]
											.singular
									)}`,
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--edit'
						},
						{
							project: props.project,
							onClick: async () => {
								const changeSet =
									props.project.logic.addChangeSet(
										new ChangeSet(
											props.project.logic,
											ProjectState.sessionAuthor,
											new Date().toISOString(),
											entity,
											false,
											StateMutationAction.RecalculateNestedCondition
										)
									);

								entity.afterAllChildrenInitialized(changeSet);

								props.project.renderAndCloseChangeSet(
									changeSet
								);
							},
							label: 'Recalculate',
							color: 'warning',
							// Bug icon
							iconEnd: 'fa-solid fa-bug',
							disabled: false,
							// Lower case the first letter of the resolved readable name
							tooltip:
								'Recalculate the state of this ' +
								lowercaseFirstLetter(
									READABLE_ENTITY_TYPES[props.entity.type]
										.shortSingular
								),
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--recalculate'
						},
						{
							project: props.project,
							onClick: async () => {
								navigator.clipboard.writeText(props.entity.id);
								popupNotification({
									text: `Copied ID: ${props.entity.id}`,
									color: 'success'
								});
							},
							label: 'Copy ID',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								'Copy this node ID to clipboard for debugging',
							parentCanvasObject: popup.self,
							id: 'copy-id-' + props.entity.id
						},
						{
							project: props.project,
							onClick: async () => {
								await props.project.recalculateValidateEntity(
									props.entity
								);
								popupNotification({
									text: `Re-validated node successfully`,
									color: 'success'
								});
							},
							label: 'Re-validate',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								"Re-validate this node's state and its errors",
							parentCanvasObject: popup.self,
							id: 're-validate-' + props.entity.id
						}
					]}
				/>
			</CanvasPopupBaseComponent>
		),
		[props.entity, props.project]
	);

	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id:
				'canvas-small-entity-card-menu-button-' +
				props.entity.id +
				'--popup',
			anchorQuerySelector:
				'#canvas-small-entity-card-menu-button-' + props.entity.id,
			arrowClasses: ['dropdown-menu-card-arrow'],
			parentCanvasObject: props.canvasElement.draggableObject,
			arrowContainerSelector:
				'#canvas-small-entity-card-menu-button-' +
				props.entity.id +
				'--popup--body'
		},
		memoizedCanvasPopupBody
	);

	useEffect(() => {
		// This is the handler for the recursive subscription
		const handleConditionUpdate = (payload: any) => {
			let hasUpdated = false;
			const newDisabledReasons: CanvasEntityTestingDisabledReason[] = [];
			const disabledReasonsToRemove: CanvasEntityTestingDisabledReason[] =
				[];

			props.project.test?.disabledConnectionsNotInTest(
				props.canvasElement.draggableObject,
				props.entity
			);

			if (
				!!props.project.test &&
				!props.project.test?.hasEntity(props.entity) &&
				!props.project.test?.getCanBeAddedToTest(props.entity)
			) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
				hasUpdated = true;
			}

			if (
				(props.project.test?.hasEntity(props.entity) ||
					props.project.test?.getCanBeAddedToTest(props.entity)) &&
				props.canvasElement.draggableObject.disabled
			) {
				props.project.canvas?.enable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				disabledReasonsToRemove.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
				hasUpdated = true;
			}

			if (
				!!props.project.test &&
				!props.project.test?.hasEntity(props.entity)
			) {
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
				hasUpdated = true;
			} else if (
				!!props.project.test &&
				props.project.test?.hasEntity(props.entity)
			) {
				disabledReasonsToRemove.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
				hasUpdated = true;
			}

			if (newDisabledReasons.length || disabledReasonsToRemove.length) {
				setDisabledReasons((currentReasons) => {
					const config = addDisabledReasons(
						currentReasons,
						newDisabledReasons
					);
					const finalConfig = removeDisabledReasons(
						config.reasons,
						disabledReasonsToRemove
					);
					return finalConfig.reasons;
				});
			}

			if (!hasUpdated) {
				forceUpdate();
			}
		};

		// This recursive function now returns a single cleanup function
		const subscribeToAllParentConditions = (
			current: ConditionState
		): (() => void) => {
			// Subscribe to this condition
			props.project.on(current.id, handleConditionUpdate);
			const cleanupThis = () =>
				props.project.off(current.id, handleConditionUpdate);

			let cleanupChild = () => {};
			// Recurse if the parent is also a condition
			if (current.parent.type === EntityType.Condition) {
				cleanupChild = subscribeToAllParentConditions(
					current.parent as ConditionState
				);
			}

			// Return a function that cleans up both self and child
			return () => {
				cleanupThis();
				cleanupChild();
			};
		};

		const handleCanvasGloballyEnabled = () => {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		};
		const handleCanvasGloballyDisabled = () => {
			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		};

		const handleTestSetupStarted = () => {
			const newDisabledReasons: CanvasEntityTestingDisabledReason[] = [];
			if (
				!!props.project.test &&
				!props.project.test?.hasEntity(props.entity) &&
				!props.project.test?.getCanBeAddedToTest(props.entity)
			) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				props.project.test?.disabledConnectionsNotInTest(
					props.canvasElement.draggableObject,
					props.entity
				);
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
			}
			if (!props.project.test?.hasEntity(props.entity)) {
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
			}
			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(
					currentReasons,
					newDisabledReasons
				);
				return config.reasons;
			});
		};

		const handleExecutionStart = () => {
			if (!!props.project.test?.hasEntity(props.entity)) {
				forceUpdate();
			}
		};

		const handleTestExited = () => {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest,
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				]);
				return config.reasons;
			});

			if (props.canvasElement.draggableObject.disabled) {
				props.project.canvas?.enable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
			}

			props.canvasElement.draggableObject.connections.forEach(
				(connection) => {
					connection.enable();
					connection.element.classList.remove('disabled');
					if (connection.complementaryLineElement) {
						connection.complementaryLineElement.classList.remove(
							'disabled'
						);
					}
				}
			);
		};

		const handleCanvasConnectionStart = (payload: any) => {
			if (!payload.compatible?.[props.entity.id]) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);

				const newOwnConnectionDisabledReasons: CanvasEntityConnectionDisabledReason[] =
					payload.incompatible?.[props.entity.id]?.reasons || [];

				setDisabledReasons((currentReasons) => {
					const config = addDisabledReasons(
						currentReasons,
						newOwnConnectionDisabledReasons
					);
					return config.reasons;
				});
			} else {
				forceUpdate();
			}
		};

		const handleCanvasConnectionEnd = () => {
			props.project.canvas?.enable(props.canvasElement.draggableObject, {
				quiet: true
			});

			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(
					currentReasons,
					ALL_CONNECTION_DISABLED_REASONS
				);
				return config.reasons;
			});
		};

		// --- Initial Setup Logic ---
		const initialNewDisabledReasons: CanvasEntityDisabledReason[] = [];
		let current: ConditionState = props.entity;
		// We must walk up the tree *during setup* to get all suggestions
		while (current) {
			// @ts-ignore
			if (current.suggestion) {
				initialNewDisabledReasons.push(
					CanvasEntityOtherDisabledReason.Suggestion
				);
			}
			if (current.parent.type !== EntityType.Condition) {
				break;
			}
			current = current.parent as ConditionState;
		}

		if (props.project.canvas?.disabled) {
			initialNewDisabledReasons.push(
				CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
			);
		}

		if (!!initialNewDisabledReasons.length) {
			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(
					currentReasons,
					initialNewDisabledReasons
				);
				return config.reasons;
			});
		} else {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		}

		// Call the recursive subscription and store its cleanup function
		const cleanupParentConditions = subscribeToAllParentConditions(
			props.entity
		);

		props.project.on(
			'canvas-globally-enabled',
			handleCanvasGloballyEnabled
		);
		props.project.on(
			'canvas-globally-disabled',
			handleCanvasGloballyDisabled
		);
		props.project.on('test-setup-started', handleTestSetupStarted);
		props.project.on('execution-start', handleExecutionStart);
		props.project.on('test-exited', handleTestExited);
		props.project.on(
			'canvas-connection-start',
			handleCanvasConnectionStart
		);
		props.project.on('canvas-connection-end', handleCanvasConnectionEnd);

		return () => {
			// Call the recursive cleanup function
			cleanupParentConditions();

			props.project.off(
				'canvas-globally-enabled',
				handleCanvasGloballyEnabled
			);
			props.project.off(
				'canvas-globally-disabled',
				handleCanvasGloballyDisabled
			);
			props.project.off('test-setup-started', handleTestSetupStarted);
			props.project.off('execution-start', handleExecutionStart);
			props.project.off('test-exited', handleTestExited);
			props.project.off(
				'canvas-connection-start',
				handleCanvasConnectionStart
			);
			props.project.off(
				'canvas-connection-end',
				handleCanvasConnectionEnd
			);
		};
	}, [
		props.project,
		props.entity,
		props.canvasElement,
		updateKey,
		setDisabledReasons,
		addDisabledReasons,
		removeDisabledReasons
	]);

	useEffect(() => {
		props.canvasElement.draggableObject.updateConnections();
	});

	const entity = props.entity;

	const dynamicInputsValueDescriptors: ValueDescriptorState[] = [];

	const commonInputButtonAllowed =
		!props.project.test &&
		!disabledReasons.includes(CanvasEntityOtherDisabledReason.Suggestion) &&
		!disabledReasons.includes(
			CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
		) &&
		!disabledReasons.includes(
			CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
		) &&
		!disabledReasons.includes(
			CanvasEntityTestingDisabledReason.CannotBeAddedToTest
		) &&
		(!props.project.onGoingConnectionNodeType ||
			(props.project.onGoingConnectionNodeType &&
				props.project.onGoingConnectionNodeType ===
					ConnectionNodeType.PropertyOut)) &&
		!disabledReasons.includes(
			CanvasEntityConnectionDisabledReason.NotInScope
		);

	const relevantDisabledReasons = [
		...props.parentDisabledReasons,
		...disabledReasons.filter(
			(reason) =>
				![CanvasEntityConnectionDisabledReason.NotInScope].includes(
					reason as any
				)
		)
	];

	const showNewDynamicInputButton = commonInputButtonAllowed;

	const showNewNestedButton =
		commonInputButtonAllowed &&
		[GroupOperatorTypes.And, GroupOperatorTypes.Or].includes(
			entity.declaration.id as GroupOperatorTypes
		);

	return (
		<div
			ref={rootElementRef}
			className={[
				'canvas-draggable-card__property-row',
				'canvas-draggable-card__nested-card',
				'canvas-draggable-card__main-input-property'
			].join(' ')}
			style={{
				...parse(resolveNestedConditionCardColor(entity)),
				position: 'relative'
			}}
			id={getCanvasEntityDOMId(props.entity)}
			{...{
				[Canvas.OBJ_ATTR_NAME]: getCanvasEntityDOMId(props.entity)
			}}
			onClick={(e) => {
				e.stopPropagation();
			}}
		>
			<div
				className={[
					'canvas-draggable-card__body',
					'no-card-shadow',
					...(!!relevantDisabledReasons.length
						? ['canvas-draggable-card__body__disabled']
						: [])
				].join(' ')}
				style={parse(resolveNestedConditionCardColor(entity))}
			>
				<div className="canvas-draggable-card__header--small">
					<div
						className={[
							'canvas-draggable-card__property__left-block',
							'canvas-draggable-card__property__value-container',
							...(!!relevantDisabledReasons.length
								? ['disabled']
								: [])
						].join(' ')}
					>
						<i
							className={`canvas-draggable-card-header__icon fa-solid fa-${
								ENTITY_FA_ICON_NAMES[entity.type] ||
								'rectangle-list'
							}`}
						/>
						<span className="canvas-draggable-card__property__labels-group">
							<p className="canvas-draggable-card__property__name">
								{safeResolveEntityName(
									entity,
									props.project.logic
								)}
							</p>
							<p className="canvas-draggable-card__property__type">
								{resolveCardSubheader(entity)}
							</p>
						</span>
					</div>
					<span
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '6px'
						}}
					>
						<div className="canvas-draggable-card__header__actions">
							<LogicErrorIconMenu
								entity={entity}
								project={props.project}
								canvasElement={props.canvasElement}
								parentDisabledReasons={relevantDisabledReasons}
							/>
							<Button
								variant="text"
								color="grey"
								iconEnd="fa-solid fa-ellipsis-v"
								size="small"
								ref={canvasPopup.anchorRef}
								className="canvas-small-entity-card-menu-button"
								id={`canvas-small-entity-card-menu-button-${entity.id}`}
								disabled={!!relevantDisabledReasons.length}
								onClick={(e: any) => {
									e.stopPropagation();
									canvasPopup?.toggle();
								}}
								onDoubleClick={(
									e: React.MouseEvent<HTMLButtonElement>
								) => {
									e.stopPropagation();
									if (relevantDisabledReasons.length) return;
									props.project?.canvas?.unselectAll();
									EditorService.navigate(
										`${props.project?.editorBasePath}/entity/${props.entity.type}/${props.entity.id}`
									);
								}}
							/>
						</div>
					</span>
				</div>

				<div>
					{entity.inputs.length > 0 && (
						<ul className="canvas-draggable-card__property-list">
							{entity.inputs
								.sort((a, b) => {
									// Sort by `.index` property
									if (a.index < b.index) {
										return -1;
									}
									if (a.index > b.index) {
										return 1;
									}
									return 0;
								})
								.map((inputMap) => {
									return (
										<InputMapProperty
											key={inputMap.id}
											inputMap={inputMap}
											entity={props.entity}
											canvasElement={props.canvasElement}
											project={props.project}
											parentDisabledReasons={
												relevantDisabledReasons
											}
											size="small"
										/>
									);
								})}
						</ul>
					)}
					{(entity.andChildrenGroup || []).length > 0 && (
						<ul className="canvas-draggable-card__property-list">
							{(entity.andChildrenGroup || [])
								.sort((a, b) => {
									// Sort by `.index` property
									if (a.index < b.index) {
										return -1;
									}
									if (a.index > b.index) {
										return 1;
									}
									return 0;
								})
								.map((nestedCondition, index) => {
									return (
										<>
											<NestedCondition
												key={nestedCondition.id}
												entity={nestedCondition}
												canvasElement={
													props.canvasElement
												}
												project={props.project}
												parentDisabledReasons={
													relevantDisabledReasons
												}
											/>
											{/* If it isn't the last item, add an 'AND' */}
											{index <
												(entity.andChildrenGroup || [])
													.length -
													1 && (
												<span
													key={
														nestedCondition.id +
														'-and-span'
													}
													className="canvas-draggable-card__property-list__and"
													style={{
														color: 'rgba(0,0,0, 0.6)',
														fontSize: '12px',
														width: '100%',
														padding: '0 14px'
													}}
												>
													AND
												</span>
											)}
										</>
									);
								})}
						</ul>
					)}
					{(entity.orChildrenGroup || []).length > 0 && (
						<ul className="canvas-draggable-card__property-list">
							{(entity.orChildrenGroup || [])
								.sort((a, b) => {
									// Sort by `.index` property
									if (a.index < b.index) {
										return -1;
									}
									if (a.index > b.index) {
										return 1;
									}
									return 0;
								})
								.map((nestedCondition, index) => {
									return (
										<>
											<NestedCondition
												key={nestedCondition.id}
												entity={nestedCondition}
												canvasElement={
													props.canvasElement
												}
												project={props.project}
												parentDisabledReasons={
													relevantDisabledReasons
												}
											/>
											{/* If it isn't the last item, add an 'AND' */}
											{index <
												(entity.orChildrenGroup || [])
													.length -
													1 && (
												<span
													key={
														nestedCondition.id +
														'-or-span'
													}
													className="canvas-draggable-card__property-list__and"
													style={{
														color: 'rgba(0,0,0, 0.6)',
														fontSize: '12px',
														width: '100%',
														padding: '0 14px'
													}}
												>
													OR
												</span>
											)}
										</>
									);
								})}
						</ul>
					)}
					{showNewNestedButton && (
						<div
							className={
								'canvas-draggable-card__action align-left'
							}
							style={{ paddingBottom: '8px' }}
						>
							<Button
								iconStart="fa-solid fa-plus"
								size="small"
								disabled={!!relevantDisabledReasons.length}
								onClick={async () => {
									// Create a new nested condition
									const changeSet =
										props.project.logic.addChangeSet(
											new ChangeSet(
												props.project.logic,
												ProjectState.sessionAuthor,
												new Date().toISOString(),
												entity,
												false,
												StateMutationAction.AddNestedAndCondition
											)
										);

									const newConditionDescriptor =
										props.project.logic.getBuiltIn(
											BUILT_IN_BASE_ENTITY_IDS.condition[
												StandaloneOperatorTypes
													.BooleanIsTrue
											].id
										) as ActionDescriptorState;

									const newConditionTransfer =
										ConditionState.new(changeSet);
									newConditionTransfer.parent =
										entity.toReference();
									newConditionTransfer.declaration =
										newConditionDescriptor.toJSON() as IActionDescriptorTransfer;
									newConditionTransfer.index = (
										entity.andChildrenGroup || []
									).length;

									const newInstance =
										createStateFromTypeAndSubscribe(
											newConditionTransfer,
											props.project.logic
										) as ConditionState;

									newInstance.hydrateAncestors();
									newInstance.addSelfToProject(changeSet);

									changeSet.add(
										newInstance,
										ChangeSetEntityChangeType.Added
									);

									newInstance.initChildren(changeSet);

									EditorService.navigate(
										`${props.project?.editorBasePath}/entity/${EntityType.Condition}/${newInstance.id}`
									);

									await props.project.renderAndCloseChangeSet(
										changeSet
									);
								}}
								style={parse('background: #7e55e6;')}
							>
								Condition
							</Button>
						</div>
					)}
					{showNewDynamicInputButton &&
						dynamicInputsValueDescriptors
							.sort((a, b) => {
								// Sort by `.index` property
								if (a.index < b.index) {
									return -1;
								}
								if (a.index > b.index) {
									return 1;
								}
								return 0;
							})
							.map((valueDescriptor) => {
								return (
									<div
										key={valueDescriptor.id}
										className={[
											'canvas-draggable-card__action-alt',
											'align-left'
										].join(' ')}
									>
										<NewDynamicInputButton
											iconStart="fa-solid fa-plus"
											size="small"
											parentDisabledReasons={
												relevantDisabledReasons
											}
											entity={
												entity as
													| OperationState
													| ConditionState
											}
											inputValueDescriptor={
												valueDescriptor
											}
											project={props.project}
											canvasElement={props.canvasElement}
											onClick={() => {
												const changeSet =
													props.project.logic.addChangeSet(
														new ChangeSet(
															props.project.logic,
															ProjectState.sessionAuthor,
															new Date().toISOString(),
															entity,
															false,
															StateMutationAction.AddInputToCondition
														)
													);

												const newInputMapTransfer =
													ActionInputMapState.new(
														changeSet
													);
												newInputMapTransfer.parent =
													entity.toReference();
												newInputMapTransfer.declaration =
													valueDescriptor.toJSON() as IValueDescriptorTransfer;

												const newInputMap =
													createStateFromTypeAndSubscribe(
														newInputMapTransfer,
														props.project.logic
													) as ValueDescriptorState;

												newInputMap.hydrateAncestors();

												changeSet.add(
													newInputMap,
													ChangeSetEntityChangeType.Added
												);

												newInputMap.addSelfToProject(
													changeSet
												);
												newInputMap.afterAllChildrenInitialized(
													changeSet
												);

												entity.afterAllChildrenInitialized(
													changeSet
												);

												EditorService.navigate(
													`${props.project?.editorBasePath}/entity/${EntityType.InputMap}/${newInputMap.id}`
												);

												props.project.renderAndCloseChangeSet(
													changeSet
												);
											}}
										>
											{safeResolveEntityName(
												valueDescriptor,
												props.project.logic
											)}
										</NewDynamicInputButton>
									</div>
								);
							})}
				</div>
			</div>
		</div>
	);
}
